import { useSonner } from 'sonner';
import type { ToastT } from 'sonner';

/**
 * ToastAnnouncer - the dashboard's refusals reach a screen reader assertively.
 *
 * WHY (board P1, 2026-09-14). Politeness is a property of the live REGION,
 * never of the node inside it. Sonner wraps every toast in a single
 * `<section aria-live="polite">`, so "Could not save the issue." was announced
 * with exactly the urgency of "Link copied": queued behind whatever the screen
 * reader was already saying. 64 of this surface's 80 toast emit sites are
 * errors (scripts/toast-policy/registry.json), the largest error surface we
 * ship.
 *
 * Sonner cannot be asked to fix it. That `aria-live="polite"` is HARDCODED in
 * the shipped artifact (node_modules/sonner/dist/index.mjs:1084, v2.0.7) and
 * `ToasterProps` exposes only `containerAriaLabel` / `closeButtonAriaLabel` -
 * no politeness, no role. Read off the installed package, not from memory. So
 * this is a second, persistent region beside the Toaster rather than a prop.
 *
 * ADDITIVE, and the cost is stated rather than hidden: Sonner keeps announcing
 * everything politely, so an error is now spoken twice - assertively when it
 * lands, then again politely when the reader next goes idle. The alternative
 * was considered and rejected: hiding Sonner's section with `aria-hidden`
 * would also strand its close button, and focusable content inside
 * `aria-hidden` is a worse defect than a repeated sentence.
 *
 * Both regions are mounted whether or not a toast is on screen. A screen
 * reader only announces a change to a region that ALREADY existed, so a region
 * minted in the same breath as its own text may not be announced at all - the
 * half of this defect a swapped attribute would have missed.
 */

/** A refusal interrupts; everything else waits. `warning` is a refusal: the
 *  dashboard resolves a partial bulk outcome to it, and "2 exported, 1 failed"
 *  is news the user has to act on. */
function isRefusal(type: ToastT['type']): boolean {
  return type === 'error' || type === 'warning';
}

/**
 * The newest toast at this politeness, rendered as text, or '' for silence.
 *
 * Deliberately does NOT fall through to an older toast when the newest one has
 * a non-string title: Sonner's title is a ReactNode, and announcing the
 * PREVIOUS refusal when the current one cannot be read aloud would tell the
 * reader about something that is no longer on screen. Silence is the honest
 * answer there.
 */
function announced(toasts: ToastT[], refusal: boolean): string {
  for (let i = toasts.length - 1; i >= 0; i -= 1) {
    const toast = toasts[i];
    if (isRefusal(toast.type) !== refusal) continue;
    const title = typeof toast.title === 'string' ? toast.title : '';
    const description = typeof toast.description === 'string' ? toast.description : '';
    return [title, description].filter(Boolean).join('. ');
  }
  return '';
}

export function ToastAnnouncer() {
  const { toasts } = useSonner();

  return (
    <>
      <div
        className="sr-only"
        data-oq-announce="assertive"
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
      >
        {announced(toasts, true)}
      </div>
      <div
        className="sr-only"
        data-oq-announce="polite"
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        {announced(toasts, false)}
      </div>
    </>
  );
}
